import { defineStore } from 'pinia'
import {
  CLIENT_ORDER_HISTORY_PAGE_SIZE,
  fetchActiveOrderApi,
  fetchClientOrderByIdApi,
  fetchClientOrderHistoryApi,
} from '@/entities/order/api/clientOrdersApi'
import { mapOrder } from '@/entities/order/model/mapOrder'
import { normalizeOrderStatus } from '@/entities/order/model/normalizeOrderStatus'
import { isTerminalOrderStatus } from '@/entities/order/model/orderStatus'
import { appendCourierPathPoint } from '@/features/client-orders/lib/appendCourierPathPoint'
import { parseCourierLocationEvent } from '@/features/client-orders/lib/parseCourierLocationEvent'

export const useClientOrdersStore = defineStore('clientOrders', {
  state: () => ({
    activeOrder: null,
    isActiveOrderLoading: false,
    activeOrderError: null,
    detailOrder: null,
    isDetailLoading: false,
    detailOrderError: null,
    orderList: [],
    orderListTotal: 0,
    isOrderListLoading: false,
    isLoadingMore: false,
    orderListError: null,
    hasLoadedOrderList: false,
  }),
  getters: {
    hasActiveOrder: (state) => Boolean(state.activeOrder),
    hasMoreOrders: (state) => state.orderList.length < state.orderListTotal,
    orderById: (state) => (orderId) => {
      const id = String(orderId ?? '')
      if (!id) {
        return null
      }
      if (state.activeOrder?.id === id) {
        return state.activeOrder
      }
      if (state.detailOrder?.id === id) {
        return state.detailOrder
      }
      return null
    },
  },
  actions: {
    clear() {
      this.activeOrder = null
      this.isActiveOrderLoading = false
      this.activeOrderError = null
      this.detailOrder = null
      this.isDetailLoading = false
      this.detailOrderError = null
      this.orderList = []
      this.orderListTotal = 0
      this.isOrderListLoading = false
      this.isLoadingMore = false
      this.orderListError = null
      this.hasLoadedOrderList = false
    },
    setActiveOrder(order) {
      const mapped = mapOrder(order)
      if (!mapped || isTerminalOrderStatus(mapped.status)) {
        this.activeOrder = null
        return
      }
      this.activeOrder = mapped
      this.activeOrderError = null
    },
    applyStatusUpdate({ order_id: orderId, status }) {
      if (!this.activeOrder || !orderId || String(this.activeOrder.id) !== String(orderId)) {
        return
      }

      const normalized = normalizeOrderStatus(status)
      if (isTerminalOrderStatus(normalized)) {
        this.activeOrder = null
        if (this.detailOrder?.id === String(orderId)) {
          this.detailOrder = {
            ...this.detailOrder,
            status: normalized,
          }
        }
        return
      }

      this.activeOrder = {
        ...this.activeOrder,
        status: normalized,
        rawStatus: status,
      }
    },
    patchCourierLocation(payload) {
      const event = parseCourierLocationEvent(payload)
      if (!event || !this.activeOrder || String(this.activeOrder.id) !== event.orderId) {
        return
      }

      const currentCourier = this.activeOrder.courier
      if (currentCourier?.id && event.courierId && currentCourier.id !== event.courierId) {
        return
      }

      const pathPoint = {
        lat: event.location.lat,
        lng: event.location.lng,
        recordedAt: event.recordedAt ?? new Date().toISOString(),
      }

      this.activeOrder = {
        ...this.activeOrder,
        courier: {
          ...(currentCourier ?? { name: null, photoUrl: null, id: null, path: [] }),
          id: event.courierId ?? currentCourier?.id ?? null,
          location: event.location,
          path: appendCourierPathPoint(currentCourier?.path, pathPoint),
        },
      }
    },
    async loadActiveOrder() {
      this.isActiveOrderLoading = true
      this.activeOrderError = null
      try {
        const order = await fetchActiveOrderApi()
        if (!order || isTerminalOrderStatus(order.status)) {
          this.activeOrder = null
        } else {
          this.activeOrder = order
        }
      } catch (error) {
        this.activeOrderError = error
        this.activeOrder = null
      } finally {
        this.isActiveOrderLoading = false
      }
    },
    async refreshActiveOrderDetails() {
      const orderId = this.activeOrder?.id
      if (!orderId) {
        return
      }

      this.isActiveOrderLoading = true
      this.activeOrderError = null
      try {
        const order = await fetchClientOrderByIdApi(orderId)
        if (!order || isTerminalOrderStatus(order.status)) {
          this.activeOrder = null
        } else {
          this.activeOrder = order
        }
      } catch (error) {
        this.activeOrderError = error
      } finally {
        this.isActiveOrderLoading = false
      }
    },
    async loadOrderDetail(orderId) {
      const id = orderId != null && orderId !== '' ? String(orderId) : ''
      if (!id) {
        this.detailOrder = null
        return
      }

      if (this.activeOrder?.id === id) {
        this.detailOrder = null
        this.detailOrderError = null
        await this.refreshActiveOrderDetails()
        return
      }

      this.isDetailLoading = true
      this.detailOrderError = null
      try {
        this.detailOrder = await fetchClientOrderByIdApi(id)
      } catch (error) {
        this.detailOrderError = error
        this.detailOrder = null
      } finally {
        this.isDetailLoading = false
      }
    },
    async loadOrdersPage(skip, { append = false } = {}) {
      const { items, total } = await fetchClientOrderHistoryApi({
        limit: CLIENT_ORDER_HISTORY_PAGE_SIZE,
        skip,
      })

      this.orderList = append ? [...this.orderList, ...items] : items
      this.orderListTotal = total
      this.hasLoadedOrderList = true
    },
    async fetchOrderList({ force = false } = {}) {
      if (this.isOrderListLoading || (this.hasLoadedOrderList && !force)) {
        return
      }

      this.isOrderListLoading = true
      this.orderListError = null
      try {
        await this.loadOrdersPage(0)
      } catch (error) {
        this.orderListError = error
      } finally {
        this.isOrderListLoading = false
      }
    },
    async loadMoreOrders() {
      if (this.isOrderListLoading || this.isLoadingMore || !this.hasMoreOrders) {
        return
      }

      this.isLoadingMore = true
      this.orderListError = null
      try {
        await this.loadOrdersPage(this.orderList.length, { append: true })
      } catch (error) {
        this.orderListError = error
      } finally {
        this.isLoadingMore = false
      }
    },
    async refreshAfterCheckout(createdOrder) {
      await this.loadActiveOrder()
      if (!this.activeOrder && createdOrder) {
        this.setActiveOrder(createdOrder)
      }
    },
  },
})
